import { useState, type CSSProperties, type MouseEvent } from 'react';
import { ReactFlow, ReactFlowProvider } from '@xyflow/react';
import type { Node, NodeTypes } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '../../components/ui/button';
import MessageNode, { type MessageNodeData } from './MessageNode';
import { useTimelineStore } from './timeline-store';
import { useTreeLayout } from './use-tree-layout';

interface TreeViewProps {
  onNavigateToNode?: (targetNodeId: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  onRefreshConversation?: () => Promise<{
    success: boolean;
    error?: string;
  }>;
}

const nodeTypes: NodeTypes = {
  message: MessageNode,
};

export default function TreeView({
  onNavigateToNode,
  onRefreshConversation,
}: Readonly<TreeViewProps>): JSX.Element {
  const nodesMap = useTimelineStore((state) => state.nodes);
  const rootNodeId = useTimelineStore((state) => state.rootNodeId);
  const isLoading = useTimelineStore((state) => state.isLoading);
  const loadError = useTimelineStore((state) => state.error);
  const [showHelp, setShowHelp] = useState(false);
  const [navigationStatus, setNavigationStatus] = useState<{
    kind: 'idle' | 'pending' | 'error';
    message: string;
  }>({ kind: 'idle', message: '' });

  const handleRefresh = async (): Promise<void> => {
    if (!onRefreshConversation || isLoading) {
      return;
    }
    await onRefreshConversation();
  };

  const { nodes, edges } = useTreeLayout(nodesMap, rootNodeId);

  const onFlowNodeClick = async (
    _event: MouseEvent,
    node: Node<MessageNodeData>,
  ): Promise<void> => {
    if (!onNavigateToNode) {
      return;
    }

    setNavigationStatus({ kind: 'pending', message: 'Navigating to message...' });
    const result = await onNavigateToNode(node.data.fullText);
    if (!result.success) {
      setNavigationStatus({
        kind: 'error',
        message: result.error ?? 'Failed to navigate to the selected message.',
      });
      return;
    }

    setNavigationStatus({ kind: 'idle', message: '' });
  };

  if (nodesMap.size === 0) {
    if (isLoading) {
      return (
        <div style={styles.emptyState}>
          <div
            className="h-7 w-7 animate-remote-spin rounded-full border-[3px] border-input border-t-foreground"
            aria-hidden
          />
          <div>Loading conversation tree…</div>
        </div>
      );
    }

    return (
      <div style={styles.emptyState}>
        <div>No conversation tree to display.</div>
        {onRefreshConversation ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void handleRefresh();
              }}
            >
              Refresh tree
            </Button>
            {loadError ? (
              <div role="alert" style={styles.emptyStateError}>
                {loadError}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div style={styles.container}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          panOnScroll
          zoomOnScroll
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
          onNodeClick={onFlowNodeClick}
        />

        {navigationStatus.kind !== 'idle' ? (
          <div
            role={navigationStatus.kind === 'error' ? 'alert' : 'status'}
            style={{
              ...styles.statusPill,
              ...(navigationStatus.kind === 'error'
                ? styles.statusPillError
                : styles.statusPillPending),
            }}
          >
            {navigationStatus.message}
          </div>
        ) : null}

        {onRefreshConversation ? (
          <button
            type="button"
            onClick={() => {
              void handleRefresh();
            }}
            disabled={isLoading}
            style={styles.refreshButton}
            title={loadError ?? 'Refresh conversation tree'}
            aria-label="Refresh conversation tree"
          >
            {isLoading ? '…' : '↻'}
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => setShowHelp((current) => !current)}
          style={{
            ...styles.helpButton,
            backgroundColor: showHelp ? '#e5e7eb' : '#fff',
          }}
          title="Navigation help"
        >
          ?
        </button>

        {showHelp ? (
          <div style={styles.helpPanel}>
            <div style={styles.helpPanelTitle}>Navigation</div>
            <div style={styles.helpSection}>
              <div style={styles.helpSectionLabel}>Trackpad</div>
              <div>Scroll: Pan</div>
              <div>Cmd + Scroll: Zoom</div>
            </div>
            <div>
              <div style={styles.helpSectionLabel}>Mouse</div>
              <div>Drag: Pan</div>
              <div>Scroll wheel: Zoom</div>
            </div>
          </div>
        ) : null}
      </div>
    </ReactFlowProvider>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
    height: '100%',
    padding: '0 24px',
    textAlign: 'center',
    fontSize: 14,
    color: '#9ca3af',
  },
  emptyStateError: {
    fontSize: 12,
    color: '#991b1b',
    maxWidth: 280,
  },
  refreshButton: {
    position: 'absolute',
    bottom: 12,
    right: 44,
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
  },
  helpButton: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    width: 24,
    height: 24,
    borderRadius: '50%',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
  },
  helpPanel: {
    position: 'absolute',
    bottom: 44,
    right: 12,
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    color: '#374151',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    minWidth: 160,
    fontSize: 14,
  },
  helpPanelTitle: {
    fontWeight: 600,
    marginBottom: 8,
  },
  helpSection: {
    marginBottom: 8,
  },
  helpSectionLabel: {
    color: '#6b7280',
    marginBottom: 2,
  },
  statusPill: {
    position: 'absolute',
    top: 12,
    left: 12,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    zIndex: 10,
  },
  statusPillPending: {
    backgroundColor: '#e0f2fe',
    color: '#0c4a6e',
    border: '1px solid #bae6fd',
  },
  statusPillError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fecaca',
  },
};
